import { useState, useEffect } from "react";
import { getProjects } from "../services/projectService";
import { getErrorMessage } from "../utils/api/errorUtils";
import { Project } from "../Pages/Inspector/InspectorFi/types/inspector.types";

// Raw shape from GET /project with x-role: inspector
// The inspector's response may embed draw info directly on each project
interface RawDraw {
  id?: string;
  drawId?: string;
  drawNumber?: string | number;
  status?: string;
}

interface RawProject {
  id?: string;
  projectId?: string;
  _id?: string;
  project?: { id?: string; name?: string; type?: string; address?: string };
  name?: string;
  type?: string;
  address?: string;
  lender?: { companyName?: string; branchName?: string };
  // possible draw fields embedded in the project list response
  drawId?: string;
  drawRequest?: RawDraw;
  draw?: RawDraw;
  draws?: RawDraw[];
  activeDraw?: RawDraw;
  currentDraw?: RawDraw;
  stakeholders?: {
    lenderBranch?: { companyName?: string };
    lender?: { companyName?: string };
  };
}

interface ApiError {
  message?: string;
  raw?: { fields?: Record<string, string> };
}

const extractProjectId = (raw: RawProject): string =>
  raw.id ?? raw.projectId ?? raw._id ?? raw.project?.id ?? "";

const extractName = (raw: RawProject): string =>
  raw.project?.name ?? raw.name ?? "";

const extractType = (raw: RawProject): string =>
  raw.project?.type ?? raw.type ?? "";

const extractAddress = (raw: RawProject): string =>
  raw.project?.address ?? raw.address ?? "";

const extractBank = (raw: RawProject): string =>
  raw.lender?.companyName ??
  raw.lender?.branchName ??
  raw.stakeholders?.lenderBranch?.companyName ??
  raw.stakeholders?.lender?.companyName ??
  "";

// Resolve drawId entirely from the project list response — no secondary API call
const extractDrawInfo = (
  raw: RawProject,
): { drawId: string; drawNum: string } => {
  // drawRequest — actual inspector project list shape
  if (raw.drawRequest?.id)
    return {
      drawId: raw.drawRequest.id,
      drawNum: String(raw.drawRequest.drawNumber ?? ""),
    };

  // Direct drawId field
  if (raw.drawId) return { drawId: raw.drawId, drawNum: "" };

  // Embedded draw object
  if (raw.draw?.id)
    return { drawId: raw.draw.id, drawNum: String(raw.draw.drawNumber ?? "") };

  if (raw.activeDraw?.id)
    return {
      drawId: raw.activeDraw.id,
      drawNum: String(raw.activeDraw.drawNumber ?? ""),
    };

  if (raw.currentDraw?.id)
    return {
      drawId: raw.currentDraw.id,
      drawNum: String(raw.currentDraw.drawNumber ?? ""),
    };

  // Draws array — pick pending/active first, fall back to last
  if (Array.isArray(raw.draws) && raw.draws.length > 0) {
    const active =
      raw.draws.find((d) => d.status === "pending" || d.status === "active") ??
      raw.draws[raw.draws.length - 1];
    return {
      drawId: active.id ?? active.drawId ?? "",
      drawNum: String(active.drawNumber ?? ""),
    };
  }

  return { drawId: "", drawNum: "" };
};

const normalizeProject = (raw: RawProject): Project | null => {
  const projectId = extractProjectId(raw);
  if (!projectId) return null;
  const { drawId, drawNum } = extractDrawInfo(raw);
  const name = extractName(raw);
  const type = extractType(raw);
  const address = extractAddress(raw);
  const bankName = extractBank(raw);
  const drawLabel = drawNum ? `Draw #${drawNum}` : "";
  return {
    id: projectId,
    drawId,
    name,
    draw: drawLabel,
    inspection: drawNum,
    bank: bankName,
    type: type.toUpperCase(),
    address,
    sub: [drawLabel, bankName].filter(Boolean).join(" · "),
    meta: [bankName, type, drawLabel && `${drawLabel} pending`]
      .filter(Boolean)
      .join(" · "),
  };
};

const useInspectorProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetches fresh data, updates state, and returns the mapped list so callers can await it.
  const refetch = async (): Promise<Project[]> => {
    setLoading(true);
    setError("");
    const { data, error: apiError } = (await getProjects({})) as {
      data: { data?: RawProject[] } | null;
      error: ApiError | null;
    };
    if (apiError) {
      setError(getErrorMessage(apiError, "Failed to load projects."));
      setLoading(false);
      return [];
    }
    const mapped = (data?.data ?? [])
      .map(normalizeProject)
      .filter((p): p is Project => p !== null);
    setProjects(mapped);
    setLoading(false);
    return mapped;
  };

  // Initial load on mount — uses a cancellation flag to avoid stale state updates.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      const { data, error: apiError } = (await getProjects({})) as {
        data: { data?: RawProject[] } | null;
        error: ApiError | null;
      };
      if (cancelled) return;
      if (apiError) {
        setError(getErrorMessage(apiError, "Failed to load projects."));
        setLoading(false);
        return;
      }
      const mapped = (data?.data ?? [])
        .map(normalizeProject)
        .filter((p): p is Project => p !== null);
      setProjects(mapped);
      setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return { projects, loading, error, refetch };
};

export default useInspectorProjects;
